import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faPlus, 
  faFilter,
  faClose,
  faChartBar,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css'
import { FadeLoader } from 'react-spinners';
import { BE_API_URL, incomeCategoryOptions, categoryOptions } from '../constants';
import { checkHealth } from '../api';
import { notifyMessage } from '../common';
const { format } = require('date-fns');

const MoneyLover = () => {
  // State variables
  const navigate = useNavigate();
  const [isHealthy, setIsHealthy] = useState(false);
  const [isCheckingHealth, setIsCheckingHealth] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showAddNew, setShowAddNew] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [transactions, setTransactions] = useState([]);
  const [totalOutcomeAmount, setTotalOutcomeAmount] = useState(0);
  const [totalIncomeAmount, setTotalIncomeAmount] = useState(0);
  const [displayValue, setDisplayValue] = useState('');
  // UI state
  const [showInputAmount] = useState(true);
  const [userOptions, setUserOptions] = useState([]);
  const [currentUser, setCurrentUser] = useState('all');
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUserId] = useState('all');

  // Health check on component mount
  useEffect(() => {
    const verifyHealth = async () => {
      try {
        const healthy = await checkHealth();
        setIsHealthy(healthy);
        if (!healthy) {
          notifyMessage('API server is not available. Please try again later.', 'error');
        }
      } catch (error) {
        console.error('Health check error:', error);
        notifyMessage('Failed to connect to the server', 'error');
        setIsHealthy(false);
      } finally {
        setIsCheckingHealth(false);
      }
    };

    verifyHealth();
  }, []);

  // Redirect or show error if health check fails
  useEffect(() => {
    if (!isCheckingHealth && !isHealthy) {
      // You can add a redirect or show an error page here if needed
      // navigate('/error');
    }
  }, [isCheckingHealth, isHealthy, navigate]);

  // Date range state
  // const toDay = new Date(); // Unused variable

  // Form handlers
  const appendCharacter = (char) => {
    setDisplayValue((prevValue) => {
      if (/[0-9.]/.test(char)) {
        const newValue = prevValue + char;
        return newValue;
      }
      return prevValue + char;
    });
  };

  const clearDisplay = () => {
    setDisplayValue('');
    setAmount('');
  };

  const calculateResult = () => {
    try {
      // Simple calculation function that only allows basic arithmetic
      const operators = ['+', '-', '*', '/', '.'];
      const sanitized = displayValue
        .split('')
        .filter(char => /[0-9+\-*/. ]/.test(char) || operators.includes(char))
        .join('');
      // eslint-disable-next-line no-new-func
      const result = new Function(`return ${sanitized}`)();
      const formattedResult = numberToCurrency(result);
      setDisplayValue(formattedResult);
      setAmount(formattedResult);
    } catch (error) {
      setDisplayValue('');
      setAmount('');
    }
  };

  const handleSelectChange = (option) => {
    setSelectedOption(option);
  };

  const handleSelectUserChange = (option) => {
    setSelectedUserOption(option);
  };
  

  // Transaction handlers
  const handleSubmitTransaction = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const date = `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`;
      const formatedAmount = amount.replace(/,/g, '');
      const transactionType = incomeCategoryOptions.includes(selectedOption.value) ? 1 : 2;

      const response = await fetch(`${BE_API_URL}/api/money-lover/create-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: formatedAmount,
          note,
          category: selectedOption.value,
          date,
          user_id: currentUser,
          transaction_type: transactionType,
          user_name: currentUser,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit transaction');
      }

      // Process response and update UI
      await response.json();
      clearForm();
      await getTransactionsByDateRange();
      notifyMessage('success', 'Added successfully!');
      setShowAddNew(false);
    } catch (error) {
      console.error('Error submitting transaction:', error);
      notifyMessage('error', 'Failed to submit transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = () => {
    setAmount('');
    setDisplayValue('');
    setNote('');
    setSelectedOption(null);
    setSelectedDate(new Date());
  };

  // Date range handlers
  // Date selection handlers
  const selectThisWeek = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6);
    setStartDate(start);
    setEndDate(end);
  };

  const selectThisMonth = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setStartDate(start);
    setEndDate(end);
  };

  const selectLastMonth = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    setStartDate(start);
    setEndDate(end);
  };

  const selectToday = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    setStartDate(start);
    setEndDate(end);
  };

  const selectYesterday = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    setStartDate(start);
    setEndDate(end);
  };

  // selectCustomDate function removed as it's not used

  // Transaction data fetch
  const getTransactionsByDateRange = useCallback(async () => {
    if (endDate < startDate) {
      notifyMessage('error', 'End date must be greater than start date');
      return;
    }

    setLoading(true);
    
    try {
      const startDateFormatted = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`;
      const endDateFormatted = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`;

      const response = await fetch(
        `${BE_API_URL}/api/money-lover/get-transactions-by-date-range?startDate=${startDateFormatted}&endDate=${endDateFormatted}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const transactionsData = await response.json();
      
      let tempTotalOutcomeAmount = 0;
      let tempTotalIncomeAmount = 0;
      
      // Process transactions data
      transactionsData.forEach(transaction => {
        if (incomeCategoryOptions.includes(transaction.category)) {
          tempTotalIncomeAmount += transaction.amount;
        } else {
          tempTotalOutcomeAmount += transaction.amount;
        }
      });
      
      setTotalOutcomeAmount(tempTotalOutcomeAmount);
      setTotalIncomeAmount(tempTotalIncomeAmount);
      setTransactions(transactionsData);
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
      notifyMessage('error', 'Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch transactions when dates change
  useEffect(() => {
    if (startDate && endDate) {
      getTransactionsByDateRange();
    }
  }, [startDate, endDate, getTransactionsByDateRange]);

  // Number formatting
  const numberToCurrency = (amount) => {
    return amount.toLocaleString('en-US');
  };

  const renderLabel = (transaction) => {
    const user = userOptions.find(user => user.value === transaction.user_uuid || user.value === transaction.user_id);
    return user ? user.label : null;
  };

  // Filter transactions based on selected user
  const filteredTransactions = selectedUserOption?.value === 'all' 
    ? transactions 
    : transactions.filter(tx => tx.user_uuid === selectedUserOption?.value || tx.user_id === selectedUserOption?.value);

  useEffect(() => {
      fetch(`${BE_API_URL}/api/v1/getUsersInGroup`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => response.json())
      .then(data => {
        const users = data.users.map(u => ({ value: u.id, label: u.name }));
        setUserOptions(users);
        if (users.length > 0) {
          setSelectedUserOption(users[0]);
          setCurrentUser(users[0].value);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
        setLoading(false);
      });
    }, []);

  // Component mount
  useEffect(() => {
    // Initialize with today's date but don't fetch yet
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    setStartDate(start);
    setEndDate(end);
    
    // The useEffect above will trigger the initial fetch
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('current_user'));
    setCurrentUser(user?.name);
    setUserOptions([{ value: user?.id, label: user?.name }]);
    setSelectedUserOption({ value: user?.id, label: user?.name });
    selectToday();
    setLoading(false);
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!accessToken || !refreshToken) {
      window.location.href = '/login';
    } else {
      const user = JSON.parse(localStorage.getItem('current_user'));
      setCurrentUser(user.id);
    }
  }, []);

  // Loading states
  if (isCheckingHealth) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <FadeLoader color="#4F46E5" loading={true} size={50} />
        <p className="text-gray-600">Connecting to server...</p>
      </div>
    );
  }

  if (!isHealthy) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 p-4 text-center">
        <div className="text-red-500 text-4xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-gray-800">Service Unavailable</h2>
        <p className="text-gray-600">We're having trouble connecting to our servers.</p>
        <p className="text-gray-500 text-sm">Please check your internet connection and try again later.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center">
        <FadeLoader color="#3b82f6" cssOverride={true} size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button className="text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon icon={faFilter} />
            </button>
            <span className="text-2xl font-bold">Money Lover</span>
          </div>
        </div>

        {/* Balance Display */}
        <div className="mt-4">
          <div className="text-gray-500 text-sm">Total Balance</div>
          <div className="text-4xl font-bold text-gray-900">
            {numberToCurrency(totalIncomeAmount - totalOutcomeAmount)}
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-5 gap-2">
          <button
            onClick={selectToday}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Today
          </button>
          <button
            onClick={selectYesterday}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Yesterday
          </button>
          <button
            onClick={selectThisWeek}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            This Week
          </button>
          <button
            onClick={selectThisMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            This Month
          </button>
          <button
            onClick={selectLastMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Last Month
          </button>

          <button
            onClick={() => setShowDateRangeModal(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center justify-center"
          >
            Custom
          </button>
        </div>
      </div>

      {/* Date Range Modal */}
      <Popup
        open={showDateRangeModal}
        onClose={() => setShowDateRangeModal(false)}
        closeOnDocumentClick
        modal
      >
        <div className="bg-white rounded-xl shadow-lg p-8 mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Select Date Range</h2>
            <button
              onClick={() => setShowDateRangeModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select start date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select end date"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDateRangeModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (startDate && endDate && startDate <= endDate) {
                    setShowDateRangeModal(false);
                    getTransactionsByDateRange();
                  } else {
                    notifyMessage('error', 'End date must be after start date');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Popup>

      {/* Summary Cards */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Income</div>
            <div className="text-2xl font-bold text-green-600">
              {numberToCurrency(totalIncomeAmount)}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Expenses</div>
            <div className="text-2xl font-bold text-red-600">
              {numberToCurrency(totalOutcomeAmount)}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="text-sm text-gray-500">Transactions</div>
            <div className="text-2xl font-bold text-gray-900">
              {transactions.length}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="w-full max-w-4xl mx-auto px-4 mb-8">
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <h2 className="text-xl font-bold text-gray-900">Recent Transactions</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="w-full sm:w-48">
                <Select
                  placeholder="Select User"
                  value={selectedUserOption}
                  options={userOptions}
                  onChange={handleSelectUserChange}
                  className="w-full"
                />
              </div>
              <button
                onClick={() => setShowAddNew(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg whitespace-nowrap"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add New
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <FadeLoader color="#3b82f6" loading={true} size={30} />
              <span className="ml-3 text-gray-600">Loading transactions...</span>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {selectedUserId === 'all' 
                ? 'No transactions found for the selected period' 
                : 'No transactions found for the selected user and period'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    incomeCategoryOptions.includes(transaction.category)
                      ? 'bg-green-100 text-green-600'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                  <div>
                    <div className="font-medium">
                      {transaction.note || transaction.category}
                    </div>
                    <div className="text-sm text-gray-800">
                      {renderLabel(transaction)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(new Date(transaction.date), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`font-semibold text-right ${
                    incomeCategoryOptions.includes(transaction.category)
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {numberToCurrency(transaction.amount)}
                  </div>
                  <Link to={`/money-lover-edit?id=${transaction.id}`} className='bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg'>
                    <FontAwesomeIcon icon={faPen} />
                  </Link>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Transaction Modal */}
      <Popup
        open={showAddNew}
        onClose={() => setShowAddNew(false)}
        closeOnDocumentClick
        modal
        contentStyle={{
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(5px)',
          borderRadius: '1rem',
          padding: '1rem',
          width: '90%',
          maxWidth: '500px',
          margin: '1rem auto',
        }}
      >
        <div className="bg-white rounded-xl shadow-lg p-8 mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Add New Transaction</h2>
            <button
              onClick={() => setShowAddNew(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          <div className="space-y-4">
            {/* Calculator Interface */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Amount</h3>
              </div>

              {showInputAmount ? (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <span className="text-gray-500">$</span>
                    </div>
                    <input
                      type="text"
                      value={displayValue}
                      readOnly
                      className="w-full pl-10 pr-4 py-3 text-right text-2xl font-bold bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    {/* Operation Buttons */}
                    <button
                      onClick={() => appendCharacter('-')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg text-red-600"
                    >
                      -
                    </button>
                    <button
                      onClick={() => appendCharacter('+')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg text-green-600"
                    >
                      +
                    </button>
                    <button
                      onClick={() => appendCharacter('.')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                    >
                      .
                    </button>
                    <button
                      onClick={clearDisplay}
                      className="bg-red-100 hover:bg-red-200 p-4 rounded-lg text-red-600"
                    >
                      C
                    </button>

                    {/* Number Buttons */}
                    {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((num) => (
                      <button
                        key={num}
                        onClick={() => appendCharacter(num.toString())}
                        className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => appendCharacter('0')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                    >
                      0
                    </button>
                    <button
                    onClick={calculateResult}
                    className="col-span-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                  >
                    =
                  </button>
                  </div>

                </div>
              ) : (
                <div>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setAmount(value);
                      setDisplayValue(value);
                    }}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                  />
                </div>
              )}
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <Select placeholder="Choose Category" value={selectedOption} options={categoryOptions} onChange={handleSelectChange} />
            </div>

            {/* Date Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
                maxDate={new Date()}
                minDate={new Date('2020-01-01')}
              />
            </div>

            {/* Note Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                className="w-full mt-8 bg-gray-50 p-4 h-30 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your note here."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={255}
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmitTransaction}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg flex items-center justify-center"
              disabled={!amount || !selectedOption || !selectedDate || isSubmitting}
            >
              <FontAwesomeIcon icon={isSubmitting ? 'spinner' : faPaperPlane} className={`mr-2 ${isSubmitting ? 'animate-spin' : ''}`} />
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </Popup>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default MoneyLover;
